"use client";

import Pricing from "@/components/Pricing";
import {
  useCredit,
  useProductWithPrices,
  useSubscription,
} from "@/hooks/usePayment";
import { Button, Card, Checkbox, Progress, ToggleSwitch } from "flowbite-react";
import Loading from "./loading";
import ManageSubscriptionButton from "@/components/ManageSubscriptionButton";
import { useMemo, useState } from "react";
import PricingCard from "@/components/PricingCard";
import axios from "axios";
import { getStripe } from "@/utils/stripe-client";

export default function Subscription() {
  const { data: subscription, isLoading: isLoadingSubscription } =
    useSubscription();

  const { data: credits, isLoading: isLoadingCredits } = useCredit();

  const { data: products = [] } = useProductWithPrices();

  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [showYearly, setShowYearly] = useState(false);

  const confirmSubscription = async () => {
    await axios.post("/api/credit/confirm", {});
  };

  const usedCreditsPercentage = useMemo(() => {
    if (isLoadingCredits) {
      return 0;
    } else {
      return (credits.spent_credits / credits.given_credits) * 100;
    }
  }, [credits, isLoadingCredits]);

  const handleSwitchPlan = async (
    priceId: string,
    priceType: string = "recurring"
  ) => {
    const { data } = await axios.post("/api/stripe/create-checkout-session", {
      price: {
        id: priceId,
        type: priceType,
      },
    });
    const { sessionId } = data;
    const stripe = await getStripe();
    stripe?.redirectToCheckout({ sessionId });
  };

  if (isLoadingSubscription || isLoadingCredits) {
    return <Loading />;
  }

  return (
    <div className="max-w-5xl w-full">
      <div className="flex flex-col gap-5">
        <div className="flex flex-row justify-between items-center">
          <p className="text-2xl">Your Active Subscription</p>
          <Button onClick={confirmSubscription}>Confirm My Subscription</Button>
        </div>

        <Card>
          <div className="flex flex-row justify-between items-center">
            <p className="text-md">{`You are on ${
              subscription ? "PRO" : "FREE"
            } Plan`}</p>
            <ManageSubscriptionButton />
          </div>

          <p className="text-gray-500">
            Start extracting and monitoring data on the web for free. Upgrade
            only when Browse AI is saving you hours of time and helping you make
            better decisions.
          </p>

          <Progress progress={usedCreditsPercentage} />
          <span>Used Credits: {credits.spent_credits}</span>
          <span>Total Credits: {credits.given_credits}</span>
        </Card>

        <div>
          <p className="mb-5 text-2xl">Plans</p>
          <div className="flex justify-center items-center">
            <span className="text-base font-medium text-gray-900 dark:text-white">
              Monthly
            </span>
            <div className="mx-5">
              <ToggleSwitch
                checked={showYearly}
                onChange={(value) => {
                  setShowYearly(value);
                }}
              />
            </div>
            <span className="text-base font-medium text-gray-500 dark:text-gray-400">
              Yearly
            </span>
          </div>

          <div className="mt-2 flex flex-row gap-2 justify-center">
            <PricingCard
              price="$0"
              name="Starter"
              unit={showYearly ? "year" : "month"}
              features={[
                "100 Credit Per Month",
                "2 Robots",
                "Daily Run",
                "2 Execution History",
              ]}
              button={
                subscription ? (
                  <ManageSubscriptionButton
                    className="my-8"
                    children="Cancel Subscription"
                  />
                ) : (
                  <span className="my-8">Already on this</span>
                )
              }
            />

            {products.map((product) => (
              <PricingCard
                price={`$${
                  (product.prices.find(
                    (price) =>
                      price.interval === (showYearly ? "year" : "month")
                  )?.unit_amount || 0) / 100
                }`}
                unit={showYearly ? "year" : "month"}
                name={product.name!}
                features={[
                  "50000 Credit Per Month",
                  "10 Robots",
                  "Hourly Run",
                  "10 Execution History",
                  "Webhook",
                ]}
                button={
                  subscription ? (
                    <span className="my-8">Already on this</span>
                  ) : (
                    <Button
                      isProcessing={isLoadingPrice}
                      disabled={isLoadingPrice}
                      onClick={() => {
                        setIsLoadingPrice(true);
                        const priceId = product.prices.find(
                          (price) =>
                            price.interval === (showYearly ? "year" : "month")
                        )?.id;
                        if (priceId) {
                          handleSwitchPlan(priceId);
                        }
                      }}
                      color="dark"
                      className="my-8"
                    >
                      Get started
                    </Button>
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
